import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { FileText, AlertTriangle, CalendarClock, TrendingUp, Clock, RefreshCcw, Wallet, Link2, Mail, UserPlus } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, StatCard, EmptyState } from "@/components/admin/ui";
import { INVOICE_STATUS_META, formatCents, fmtDate } from "./billingMeta";

const QuickAction = ({ icon: Icon, label, helper, onClick, disabled, testId }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    title={disabled ? "Coming soon" : undefined}
    className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 dark:border-white/10 dark:bg-[#111C33]"
  >
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-[#0052FF] dark:bg-blue-500/10 dark:text-blue-400">
      <Icon size={18} />
    </span>
    <div className="min-w-0">
      <p className="text-sm font-bold text-slate-900 dark:text-white">{label}</p>
      <p className="truncate text-xs text-slate-500 dark:text-slate-400">{disabled ? "Coming soon" : helper}</p>
    </div>
  </button>
);

export const BillingOverviewTab = ({ API, canManage, onViewInvoices, onViewPayments, onCreateInvoice, onViewCustomers }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/org/billing/overview`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API]);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="billing-overview-error">{error}</p>;
  if (!data) return <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="billing-overview-loading">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="billing-overview-tab">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={FileText} tone="blue" label="Outstanding Balance" value={formatCents(data.outstanding_cents)} helper={data.outstanding_invoice_count ? `Across ${data.outstanding_invoice_count} invoice${data.outstanding_invoice_count !== 1 ? "s" : ""}` : "No outstanding invoices"} testId="billing-stat-outstanding" />
        <StatCard icon={AlertTriangle} tone="orange" label="Overdue" value={formatCents(data.overdue_cents)} helper={data.overdue_count ? `${data.overdue_count} invoice${data.overdue_count !== 1 ? "s" : ""} overdue` : "No overdue invoices"} testId="billing-stat-overdue" />
        <StatCard icon={CalendarClock} tone="purple" label="Due Soon" value={formatCents(data.due_soon_cents)} helper={data.due_soon_count ? `${data.due_soon_count} invoice${data.due_soon_count !== 1 ? "s" : ""} (next 14 days)` : "No invoices due soon"} testId="billing-stat-due-soon" />
        <StatCard icon={TrendingUp} tone="green" label="Collected This Month" value={formatCents(data.collected_this_month_cents)} helper={data.collected_this_month_cents === 0 ? "No payments collected yet" : data.collected_change_pct == null ? "No prior month to compare" : `${data.collected_change_pct >= 0 ? "↑" : "↓"} ${Math.abs(data.collected_change_pct)}% vs. last month`} testId="billing-stat-collected" />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Panel title="Recent Invoices" action={<button type="button" onClick={() => onViewInvoices()} data-testid="billing-overview-view-all-invoices" className="text-xs font-bold text-[#0052FF] hover:underline">View all</button>}>
            {data.recent_invoices.length === 0 ? (
              <EmptyState icon={FileText} title="No invoices yet." message="Invoices you create and send will show up here." testId="billing-overview-invoices-empty" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="billing-overview-invoices-table">
                  <thead><tr className="text-left text-xs font-bold uppercase tracking-wide text-slate-400"><th className="pb-2">Invoice #</th><th className="pb-2">Customer</th><th className="pb-2">Amount</th><th className="pb-2">Status</th><th className="pb-2">Due Date</th></tr></thead>
                  <tbody>
                    {data.recent_invoices.map((inv) => (
                      <tr key={inv.invoice_id} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50 dark:border-white/5 dark:hover:bg-white/5" onClick={() => onViewInvoices(inv.invoice_id)} data-testid={`billing-overview-invoice-row-${inv.invoice_id}`}>
                        <td className="py-2 font-bold text-[#0052FF]">{inv.display_id}</td>
                        <td className="py-2">{inv.customer_snapshot.first_name} {inv.customer_snapshot.last_name}</td>
                        <td className="py-2 font-semibold">{formatCents(inv.total_cents)}</td>
                        <td className="py-2"><span className={`rounded-full border px-2 py-0.5 text-xs font-bold ${INVOICE_STATUS_META[inv.effective_status].cls}`}>{INVOICE_STATUS_META[inv.effective_status].label}</span></td>
                        <td className="py-2 text-slate-500 dark:text-slate-400">{fmtDate(inv.due_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel title="Recent Payments" action={<button type="button" onClick={onViewPayments} data-testid="billing-overview-view-all-payments" className="text-xs font-bold text-[#0052FF] hover:underline">View all</button>}>
            {data.recent_payments.length === 0 ? (
              <EmptyState icon={Wallet} title="No payments recorded yet." message="Payments applied to invoices will show up here." testId="billing-overview-payments-empty" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="billing-overview-payments-table">
                  <thead><tr className="text-left text-xs font-bold uppercase tracking-wide text-slate-400"><th className="pb-2">Date</th><th className="pb-2">Invoice #</th><th className="pb-2">Amount</th><th className="pb-2">Method</th></tr></thead>
                  <tbody>
                    {data.recent_payments.map((p) => (
                      <tr key={p.payment_id} className="border-t border-slate-100 dark:border-white/5" data-testid={`billing-overview-payment-row-${p.payment_id}`}>
                        <td className="py-2">{fmtDate(p.received_at)}</td>
                        <td className="py-2 font-bold text-[#0052FF]">{p.invoice_display_id}</td>
                        <td className="py-2 font-semibold">{formatCents(p.amount_cents)}</td>
                        <td className="py-2 text-slate-500 dark:text-slate-400">{p.method}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel title="Revenue & Collections" subtitle="Last 6 months">
            {!data.has_any_data ? (
              <EmptyState icon={TrendingUp} title="No billing activity yet." message="Your invoiced vs. collected revenue will chart here once you send invoices and record payments." testId="billing-overview-chart-empty" />
            ) : (
              <div className="h-64" data-testid="billing-overview-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.chart.map((c) => ({ ...c, invoiced: c.invoiced_cents / 100, collected: c.collected_cents / 100 }))}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-slate-100 dark:stroke-white/5" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                    <Legend />
                    <Bar dataKey="collected" name="Collected" fill="#0052FF" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="invoiced" name="Invoiced" fill="#B7C6FF" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Panel>
        </div>

        <div className="space-y-6">
          <Panel title="Needs Attention">
            {data.needs_attention.length === 0 ? (
              <EmptyState icon={RefreshCcw} title="All caught up." message="Nothing needs your attention right now." testId="billing-needs-attention-empty" />
            ) : (
              <div className="space-y-2" data-testid="billing-needs-attention-list">
                {data.needs_attention.map((item) => {
                  const Icon = item.type === "overdue" ? AlertTriangle : Clock;
                  return (
                    <button key={item.type} type="button" onClick={() => onViewInvoices()} data-testid={`billing-needs-attention-${item.type}`} className="flex w-full items-center gap-3 rounded-xl border border-slate-200 p-3 text-left transition-colors duration-150 hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5">
                      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${item.type === "overdue" ? "bg-red-50 text-red-500 dark:bg-red-500/10" : "bg-amber-50 text-amber-500 dark:bg-amber-500/10"}`}><Icon size={16} /></span>
                      <div><p className="text-sm font-bold text-slate-900 dark:text-white">{item.label}</p><p className="text-xs text-slate-500 dark:text-slate-400">{item.detail}</p></div>
                    </button>
                  );
                })}
              </div>
            )}
          </Panel>

          <Panel title="Quick Actions">
            <div className="grid gap-3">
              <QuickAction icon={FileText} label="Create Invoice" helper="Generate and send a customer invoice" onClick={onCreateInvoice} disabled={!canManage} testId="billing-quick-action-create-invoice" />
              <QuickAction icon={Link2} label="Create Payment Link" helper="Send a secure payment link" disabled testId="billing-quick-action-payment-link" />
              <QuickAction icon={Mail} label="Send Payment Reminder" helper="Notify customers of due invoices" disabled testId="billing-quick-action-reminder" />
              <QuickAction icon={UserPlus} label="Add Customer" helper="Create a new customer record" onClick={onViewCustomers} disabled={!canManage} testId="billing-quick-action-add-customer" />
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
};
